import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocationSilo, OrderingInformation, Silo } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DIALOG_RETURN_TYPES, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { RepeatOrderItem } from 'src/app/models/repeat-order-item.model';

import { ProductSiloComponent } from 'src/app/product/components/product-silo/product-silo.component';
import * as ProductActions from 'src/app/product/store/actions';
import * as ProductSelectors from 'src/app/product/store/selectors';

import * as AppActions from 'src/app/store/actions';

interface RepeatOrderSiloData extends AbstractDialogData {
  repeatOrderItem?: RepeatOrderItem;
  quantity?: number;
}

@UntilDestroy()
@Component({
  selector: 'app-repeat-order-silo',
  templateUrl: 'silo.component.html',
  styleUrls: ['silo.component.scss'],
  imports: [AgrDialogModule, ProductSiloComponent],
})
export class RepeatOrderSiloDistributionComponent extends AbstractDialog<RepeatOrderSiloData> implements OnInit {
  orderingInformation: OrderingInformation;
  selectedSilos: Silo[] = [];
  private silos$: Observable<LocationSilo[]> = this.store.select(ProductSelectors.selectSilos);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RepeatOrderSiloData,
    protected readonly mdDialogRef: MatDialogRef<RepeatOrderSiloDistributionComponent, AbstractDialogResultData<RepeatOrderSiloData>>,
    private readonly localeService: LocaleService,
    private readonly store: Store,
  ) {
    super();
    this.orderingInformation = this.data.repeatOrderItem.orderingInformation;
  }

  ngOnInit(): void {
    this.data.title = this.localeService.translate('silo.tab-title');
    this.data.subTitle = this.data.repeatOrderItem?.title;
    this.data.confirmButton = true;
    this.data.confirmButtonText = this.localeService.translate('silo.confirm');

    this.store.dispatch(ProductActions.getSilosRequest());

    this.silos$.pipe(untilDestroyed(this)).subscribe((silos) => {
      if (silos?.length) {
        this.setSilos(silos);
      }
    });
  }

  cancel(): void {
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel });
  }

  confirmSiloDistribution(): void {
    const internalId = this.data.repeatOrderItem.internalId;
    this.store.dispatch(AppActions.updateSiloDistribution({ silos: this.selectedSilos, internalId }));
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm });
  }

  handleSilosChange(silos: Silo[]): void {
    this.selectedSilos = silos;
  }

  handleSilosAreValidChange(silosAreValid: boolean): void {
    this.data.disableConfirmButton = !silosAreValid;
  }

  private setSilos(silos: LocationSilo[]): void {
    if (this.data.repeatOrderItem.silos) {
      this.setSilosBasedOnUserSelection();
    } else {
      this.setSilosBasedOnPreviousOrder(silos);
    }
  }

  private setSilosBasedOnUserSelection(): void {
    for (const selectedSilo of this.data.repeatOrderItem.silos) {
      this.selectedSilos.push(selectedSilo);
    }
  }

  private setSilosBasedOnPreviousOrder(locationSilos: LocationSilo[]): void {
    const findLocationSilo = (silo: Silo): LocationSilo => locationSilos.find((ls) => ls.siloNumber === silo.siloNumber);

    this.data.repeatOrderItem.silos.forEach((silo) => {
      const locationSilo = findLocationSilo(silo);
      if (locationSilo) {
        const newSilo: Silo = {
          id: locationSilo.id,
          name: locationSilo.siloNumber,
          siloNumber: locationSilo.siloNumber,
          distributionType: silo.distributionType,
          quantity: silo.quantity,
        };

        this.selectedSilos.push(newSilo);
      }
    });
  }
}
